// src/redux/influencerDetailsSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getInfluencerDetails,
  enrichInstagram,
  enrichYoutube,
} from "../services/influencerSearchService";
import { mapApiInfluencerToUiModel } from "../mappers/influencerMapper";

/* -----------------------------
   Helpers
------------------------------ */

const FRESH_MS = 60_000;

const toErrorMessage = (err, fallback) => {
  if (!err) return fallback;
  if (typeof err === "string") return err;
  return err?.message || err?.error || err?.detail || fallback;
};

const normalizeId = (value) => String(value || "").trim();

const normalizeUsername = (value) =>
  String(value || "")
    .trim()
    .replace(/^@/, "");

const normalizeArg = (arg) => {
  if (typeof arg === "string") {
    return {
      influencerId: normalizeId(arg),
      force: false,
    };
  }

  return {
    influencerId: normalizeId(arg?.influencerId),
    force: !!arg?.force,
  };
};

const isDynamoPrimaryKey = (value) => /^INF#[A-Z]+#/i.test(String(value || ""));

const resolveCanonicalId = (data) => {
  if (!data) return null;

  const platform = String(data?.platform || "").toLowerCase();

  if (platform === "instagram") {
    const rawId = data?.raw_source?.id || data?.rawSource?.id;

    return (
      normalizeId(data?.external_id) ||
      normalizeId(rawId) ||
      (!isDynamoPrimaryKey(data?.id) && /^\d+$/.test(String(data?.id || ""))
        ? normalizeId(data?.id)
        : null) ||
      normalizeUsername(data?.username) ||
      normalizeUsername(data?.handle) ||
      null
    );
  }

  if (platform === "youtube") {
    return (
      normalizeId(data?.channelId) ||
      normalizeId(data?.channel_id) ||
      normalizeId(data?.raw_source?.channel_id) ||
      normalizeId(data?.rawSource?.channel_id) ||
      normalizeId(data?.external_id) ||
      (!isDynamoPrimaryKey(data?.id) ? normalizeId(data?.id) : null) ||
      null
    );
  }

  return (
    normalizeId(data?.external_id) ||
    normalizeId(data?.channelId) ||
    normalizeId(data?.channel_id) ||
    (!isDynamoPrimaryKey(data?.id) ? normalizeId(data?.id) : null) ||
    normalizeUsername(data?.username) ||
    null
  );
};

const getRouteIdentity = (data) => {
  if (!data) return null;

  return (
    normalizeId(data?._routeId) ||
    normalizeId(data?.routeId) ||
    resolveCanonicalId(data) ||
    normalizeUsername(data?.username) ||
    null
  );
};

const hasArray = (value) => Array.isArray(value) && value.length > 0;

const looksDetailedEnough = (influencer) => {
  if (!influencer) return false;

  if (influencer.details_loaded === true || influencer.detailsLoaded === true) {
    return true;
  }

  if (influencer.detail_fetched_at || influencer.detailFetchedAt) {
    return true;
  }

  if (influencer.raw_source || influencer.rawSource) {
    return true;
  }

  if (hasArray(influencer.recent_posts) || hasArray(influencer.recentPosts)) {
    return true;
  }

  if (
    influencer.last_refreshed_at ||
    influencer.updated_at ||
    influencer.ai_relevance_reason ||
    influencer.avg_engagement_rate_90d != null ||
    influencer.posts_last_30d != null ||
    influencer.posts_per_week_30d != null
  ) {
    return true;
  }

  return false;
};

const extractFirstItem = (payload) => {
  if (!payload) return null;

  if (Array.isArray(payload?.items) && payload.items.length) {
    return payload.items[0];
  }

  if (Array.isArray(payload?.data?.items) && payload.data.items.length) {
    return payload.data.items[0];
  }

  if (Array.isArray(payload?.body?.items) && payload.body.items.length) {
    return payload.body.items[0];
  }

  return null;
};

const mapDetails = (data, routeId) => {
  const mapped = mapApiInfluencerToUiModel(data, { routeId }) || data;

  return {
    ...mapped,
    details_loaded: true,
    detail_fetched_at: new Date().toISOString(),
  };
};

const mergeInfluencer = (current, incoming) => {
  if (!current) return incoming;
  if (!incoming) return current;

  return {
    ...current,
    ...incoming,

    // Avoid losing detail arrays when a later payload is smaller.
    recent_posts:
      incoming.recent_posts ??
      incoming.recentPosts ??
      current.recent_posts ??
      current.recentPosts ??
      undefined,

    recentPosts:
      incoming.recentPosts ??
      incoming.recent_posts ??
      current.recentPosts ??
      current.recent_posts ??
      undefined,

    raw_source: incoming.raw_source ?? current.raw_source ?? null,
    rawSource: incoming.rawSource ?? current.rawSource ?? null,
  };
};

const isSameEntity = (state, influencerId) => {
  const id = normalizeId(influencerId);

  if (!id) return false;

  return (
    normalizeId(state.routeId) === id ||
    normalizeId(state.canonicalId) === id ||
    resolveCanonicalId(state.current) === id ||
    getRouteIdentity(state.current) === id
  );
};

/* -----------------------------
   Thunks
------------------------------ */

export const fetchInfluencerDetailsAsync = createAsyncThunk(
  "influencerDetails/fetchInfluencerDetails",
  async (arg, { rejectWithValue }) => {
    try {
      const { influencerId } = normalizeArg(arg);

      if (!influencerId) {
        throw new Error("influencerId is required");
      }

      const data = await getInfluencerDetails(influencerId);

      return {
        influencerId,
        data,
      };
    } catch (error) {
      return rejectWithValue(
        toErrorMessage(error, "Failed to fetch influencer details")
      );
    }
  },
  {
    condition: (arg, { getState }) => {
      const { influencerId, force } = normalizeArg(arg);

      if (!influencerId) return false;

      const state = getState()?.influencerDetails || {};

      if (
        state.requestedId === influencerId &&
        (state.loading || state.updating)
      ) {
        return false;
      }

      if (!force && isSameEntity(state, influencerId) && state.lastFetchedAt) {
        const age = Date.now() - Number(state.lastFetchedAt || 0);

        if (age < FRESH_MS && looksDetailedEnough(state.current)) {
          return false;
        }
      }

      return true;
    },
  }
);

export const refreshInfluencerAsync = createAsyncThunk(
  "influencerDetails/refreshInfluencer",
  async (
    { influencerId, platform, username, citySlug, nicheSlug },
    { rejectWithValue }
  ) => {
    try {
      const safePlatform = String(platform || "").toLowerCase();
      const safeInfluencerId = normalizeId(influencerId);
      const safeUsername = normalizeUsername(username);
      const safeCitySlug = normalizeId(citySlug) || "unknown";
      const safeNicheSlug = normalizeId(nicheSlug) || "unknown";

      if (safePlatform === "instagram") {
        if (!safeUsername) {
          throw new Error("Cannot refresh Instagram data: username not available.");
        }

        const enrichResult = await enrichInstagram({
          username: safeUsername,
          niche_slug: safeNicheSlug,
          city_slug: safeCitySlug,
          persist: true,
        });

        const enrichedItem = extractFirstItem(enrichResult);
        const enrichedId = resolveCanonicalId(enrichedItem);

        const refetchId = safeInfluencerId || enrichedId || safeUsername;

        try {
          const data = await getInfluencerDetails(refetchId);

          return {
            influencerId: refetchId,
            data,
            refreshedFrom: "details",
          };
        } catch (detailsError) {
          if (enrichedItem) {
            return {
              influencerId: enrichedId || refetchId,
              data: enrichedItem,
              refreshedFrom: "enrich-result",
            };
          }

          throw detailsError;
        }
      }

      if (safePlatform === "youtube") {
        const channelId = safeInfluencerId || safeUsername;

        if (!channelId) {
          throw new Error("influencerId/channel id is required for YouTube refresh");
        }

        const enrichResult = await enrichYoutube({
          channel_ids: [channelId],
          niche_slug: safeNicheSlug,
          city_slug: safeCitySlug,
          priority: 5,
        });

        const enrichedItem = extractFirstItem(enrichResult);
        const enrichedId = resolveCanonicalId(enrichedItem);
        const refetchId = enrichedId || channelId;

        try {
          const data = await getInfluencerDetails(refetchId);

          return {
            influencerId: refetchId,
            data,
            refreshedFrom: "details",
          };
        } catch (detailsError) {
          if (enrichedItem) {
            return {
              influencerId: refetchId,
              data: enrichedItem,
              refreshedFrom: "enrich-result",
            };
          }

          throw detailsError;
        }
      }

      throw new Error(`Unsupported platform: ${safePlatform || "unknown"}`);
    } catch (error) {
      return rejectWithValue(
        toErrorMessage(error, "Failed to refresh influencer data")
      );
    }
  }
);

/* -----------------------------
   State
------------------------------ */

const initialState = {
  current: null,

  routeId: null,
  canonicalId: null,
  requestedId: null,

  lastFetchedAt: null,
  lastRefreshedAt: null,

  loading: false,
  updating: false,
  refreshing: false,

  error: null,
  refreshError: null,
};

/* -----------------------------
   Slice
------------------------------ */

const influencerDetailsSlice = createSlice({
  name: "influencerDetails",
  initialState,
  reducers: {
    hydrateFromSummary: (state, action) => {
      const payload = action.payload || null;
      const influencer = payload?.influencer ?? payload ?? null;
      const routeId = normalizeId(payload?.routeId);

      if (!influencer) return;

      const mapped = influencer?.metrics
        ? influencer
        : mapApiInfluencerToUiModel(influencer, { routeId }) || influencer;

      const incomingRouteId =
        routeId ||
        normalizeId(mapped?._routeId) ||
        normalizeId(mapped?.routeId) ||
        normalizeUsername(mapped?.username) ||
        resolveCanonicalId(mapped) ||
        null;

      const incomingCanonicalId = resolveCanonicalId(mapped);

      const sameEntity =
        !state.current ||
        isSameEntity(state, incomingRouteId) ||
        isSameEntity(state, incomingCanonicalId) ||
        resolveCanonicalId(state.current) === incomingCanonicalId;

      state.current = sameEntity ? mergeInfluencer(state.current, mapped) : mapped;

      state.routeId = incomingRouteId || state.routeId;
      state.canonicalId = incomingCanonicalId || state.canonicalId;

      state.error = null;
      state.refreshError = null;
    },

    clearDetails: () => ({ ...initialState }),

    clearDetailsErrors: (state) => {
      state.error = null;
      state.refreshError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchInfluencerDetailsAsync.pending, (state, action) => {
        const { influencerId } = normalizeArg(action.meta.arg);
        const sameEntity = isSameEntity(state, influencerId);

        state.requestedId = influencerId;
        state.routeId = influencerId;

        // Critical fix: do not show the previous creator while loading a new route.
        if (!sameEntity) {
          state.current = null;
          state.canonicalId = null;
          state.loading = true;
          state.updating = false;
        } else {
          state.loading = !state.current;
          state.updating = !!state.current;
        }

        state.error = null;
      })

      .addCase(fetchInfluencerDetailsAsync.fulfilled, (state, action) => {
        const { influencerId, data } = action.payload || {};

        if (state.requestedId && influencerId !== state.requestedId) {
          return;
        }

        if (!data) {
          state.loading = false;
          state.updating = false;
          return;
        }

        const mapped = mapDetails(data, influencerId);
        const canonicalId = resolveCanonicalId(mapped);

        state.current = mergeInfluencer(state.current, mapped);
        state.routeId = influencerId || state.routeId;
        state.canonicalId = canonicalId || state.canonicalId;

        state.lastFetchedAt = Date.now();
        state.loading = false;
        state.updating = false;
        state.error = null;
      })

      .addCase(fetchInfluencerDetailsAsync.rejected, (state, action) => {
        state.loading = false;
        state.updating = false;
        state.error = action.payload || "Failed to fetch influencer details.";
      })

      .addCase(refreshInfluencerAsync.pending, (state) => {
        state.refreshing = true;
        state.refreshError = null;
      })

      .addCase(refreshInfluencerAsync.fulfilled, (state, action) => {
        const { data, influencerId: refetchedId } = action.payload || {};

        if (data) {
          const routeId = state.routeId || refetchedId;
          const mapped = mapDetails(data, routeId);
          const canonicalId = resolveCanonicalId(mapped);

          state.current = mergeInfluencer(state.current, mapped);
          state.canonicalId =
            canonicalId ||
            resolveCanonicalId({ id: refetchedId, platform: mapped?.platform }) ||
            state.canonicalId;

          state.lastFetchedAt = Date.now();
          state.lastRefreshedAt = new Date().toISOString();
        }

        state.refreshing = false;
        state.refreshError = null;
        state.error = null;
      })

      .addCase(refreshInfluencerAsync.rejected, (state, action) => {
        state.refreshing = false;
        state.refreshError =
          action.payload || "Failed to refresh influencer data.";

        // Keep existing page visible; surface refresh failure separately.
        state.error = state.error || null;
      });
  },
});

export const { hydrateFromSummary, clearDetails, clearDetailsErrors } =
  influencerDetailsSlice.actions;

export default influencerDetailsSlice.reducer;